import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Child } from '../../types';
import { Icon } from '../../components/icons';
import { formatAccountNumber } from '../../utils/format';

interface AccountMapping {
    accountNumber: string;
    children: Child[];
}

/**
 * Manage which children a bank account pays for. Accounts with several
 * children (e.g. twins) get their transactions routed to manual split
 * assignment instead of being auto-assigned.
 */
const AccountMappingsManager: React.FC = () => {
    const [mappings, setMappings] = useState<AccountMapping[]>([]);
    const [children, setChildren] = useState<Child[]>([]);
    const [selectedChild, setSelectedChild] = useState<{ [account: string]: string }>({});

    const loadMappings = useCallback(() => {
        axios.get<AccountMapping[]>('/api/account-mappings')
            .then(response => setMappings(response.data));
    }, []);

    useEffect(() => {
        loadMappings();
        axios.get<Child[]>('/api/data/children')
            .then(response => setChildren(response.data));
    }, [loadMappings]);

    const addChild = (accountNumber: string) => {
        const childId = selectedChild[accountNumber];
        if (!childId) return;
        axios.post('/api/account-mappings', null, { params: { accountNumber, childId } })
            .then(() => {
                setSelectedChild(prev => ({ ...prev, [accountNumber]: '' }));
                loadMappings();
            });
    };

    const removeChild = (accountNumber: string, childId: number, childName: string) => {
        if (!window.confirm(`Usunąć przypisanie konta do: ${childName}?`)) return;
        axios.delete('/api/account-mappings', { params: { accountNumber, childId } })
            .then(loadMappings);
    };

    if (mappings.length === 0) {
        return null;
    }

    return (
        <section className="section">
            <h2 className="section-title">Konta bankowe</h2>
            <p className="section-hint">
                Konto przypisane do kilku dzieci (np. bliźniaki) kieruje przelewy do ręcznego podziału
                w sekcji „Niedopasowane transakcje”.
            </p>
            {mappings.map(mapping => {
                const available = children.filter(c => !mapping.children.some(mc => mc.id === c.id));
                return (
                    <div key={mapping.accountNumber} className="account-mapping-card surface">
                        <div className="account-number">{formatAccountNumber(mapping.accountNumber)}</div>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            {mapping.children.map(child => (
                                <span key={child.id} className="mapping-chip">
                                    {child.name}
                                    <button
                                        type="button"
                                        title={`Usuń przypisanie: ${child.name}`}
                                        aria-label={`Usuń przypisanie: ${child.name}`}
                                        onClick={() => removeChild(mapping.accountNumber, child.id, child.name)}
                                    ><Icon name="x" size={14} /></button>
                                </span>
                            ))}
                            <select
                                className="form-select form-select-sm w-auto"
                                aria-label="Dodaj dziecko do konta"
                                value={selectedChild[mapping.accountNumber] || ''}
                                onChange={e => setSelectedChild(prev => ({ ...prev, [mapping.accountNumber]: e.target.value }))}
                            >
                                <option value="">+ dodaj dziecko…</option>
                                {available.map(child => (
                                    <option key={child.id} value={child.id}>{child.name}</option>
                                ))}
                            </select>
                            {selectedChild[mapping.accountNumber] && (
                                <button className="btn btn-sm btn-primary" onClick={() => addChild(mapping.accountNumber)}>
                                    Dodaj
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </section>
    );
};

export default AccountMappingsManager;
